import { useEffect, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

import { ENTITY_TYPES, type EntityType, NODE_TYPES, SERVICE_TYPE_TO_ENTITY_TYPE_MAP } from '@/shared/api/openmetadata';
import type { AssetTreeNode } from '@/shared/lib/tree';

type UseAssetTreeAutoSelectProps = {
  treeData: AssetTreeNode[];
  expandedKeys: React.Key[];
  onExpand: (keys: React.Key[], info: { expanded: boolean; node: unknown }) => void;
  onSelect: (keys: React.Key[], info: { node: { key: React.Key } }) => void;
};

type FindNodeResult = {
  node: AssetTreeNode | null;
  parentNode: AssetTreeNode | null;
};

const SERVICE_TO_ENTITY_MAP: Record<string, EntityType> = {
  databaseService: ENTITY_TYPES.DATABASE,
  mlmodelService: ENTITY_TYPES.MLMODEL,
  storageService: ENTITY_TYPES.CONTAINER,
  messagingService: ENTITY_TYPES.TOPIC,
} as const;

const findEntityTypeNode = (nodes: AssetTreeNode[], serviceType: string): AssetTreeNode | null => {
  const serviceTypeKey = SERVICE_TYPE_TO_ENTITY_TYPE_MAP[serviceType.toLowerCase()];
  if (!serviceTypeKey) return null;

  const entityType = SERVICE_TO_ENTITY_MAP[serviceTypeKey];
  if (!entityType) return null;

  return nodes.find((node) => node.type === NODE_TYPES.ENTITY_TYPE && node.serviceType === entityType) || null;
};

const findServiceTypeNodeInChildren = (parentNode: AssetTreeNode, serviceType: string): AssetTreeNode | null => {
  if (!parentNode.children || parentNode.children.length === 0) return null;

  const children = parentNode.children as AssetTreeNode[];
  return (
    children.find(
      (child) =>
        child.type === NODE_TYPES.SERVICE_TYPE && child.serviceType?.toLowerCase() === serviceType.toLowerCase()
    ) || null
  );
};

const findNodeForServiceType = (nodes: AssetTreeNode[], serviceType: string): FindNodeResult => {
  const parentNode = findEntityTypeNode(nodes, serviceType);
  if (!parentNode) return { node: null, parentNode: null };

  const node = findServiceTypeNodeInChildren(parentNode, serviceType);
  return { node, parentNode };
};

export const useAssetTreeAutoSelect = ({ treeData, expandedKeys, onExpand, onSelect }: UseAssetTreeAutoSelectProps) => {
  const [searchParams] = useSearchParams();
  const processedRef = useRef(false);
  const selectedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current || treeData.length === 0) return;

    const dataSourceParam = searchParams.get('dataSource');
    if (!dataSourceParam) return;

    const { node, parentNode } = findNodeForServiceType(treeData, dataSourceParam);

    if (!parentNode) return;

    const isParentExpanded = expandedKeys.includes(parentNode.key);

    if (!isParentExpanded) {
      const newExpandedKeys = [...expandedKeys, parentNode.key];
      onExpand(newExpandedKeys, {
        expanded: true,
        node: parentNode,
      });
      return;
    }

    if (!node) return;

    if (!selectedRef.current) {
      onSelect([node.key], { node: { key: node.key } });
      selectedRef.current = true;
    }

    if (node.isLeaf) {
      processedRef.current = true;
      return;
    }

    const isNodeExpanded = expandedKeys.includes(node.key);
    if (!isNodeExpanded) {
      const newExpandedKeys = [...expandedKeys, node.key];
      onExpand(newExpandedKeys, {
        expanded: true,
        node: node,
      });
    }

    processedRef.current = true;
  }, [treeData, expandedKeys, searchParams, onExpand, onSelect]);
};
